import React, { createElement, Fragment, useEffect, useRef, useState } from 'react'
import { autocomplete } from '@algolia/autocomplete-js'
import algoliasearch from 'algoliasearch/lite'
import { get } from 'lodash'
import { render } from 'react-dom'

const AlgoliaHandler = (props: any) => {
  const containerRef = useRef(null)
  const [search, setSearch] = useState(null) as any
  const [enterState, setEnterState] = useState(null) as any

  useEffect(() => {
    if (!search && containerRef.current) {
      setSearch(
        autocomplete({
          autoFocus: false,
          openOnFocus: true,
          container: containerRef.current,
          placeholder: 'Search...',
          renderer: { createElement, Fragment },
          ...props,
          render({ children }: any, root) {
            render(children, root)
          },
          ...props,
          initialState: { query: props.initialValue },
          onStateChange: ({ prevState, state }) => {
            if (prevState.activeItemId !== null && state.activeItemId === null) {
              setEnterState(prevState)
            }
            if (prevState.query !== state.query) {
              props.onChange(state.query)
              setEnterState(null)
            }
          },
          onReset: () => setEnterState(null),
        })
      )

      return () => {
        if (search) {
          search.destroy()
        }
      }
    }
  }, [props, search, setSearch, setEnterState])

  useEffect(() => {
    if (search) {
      search.setQuery(props.initialValue)
    }
  }, [search, props.initialValue])

  return (
    <div
      ref={containerRef}
      onKeyDown={(e) => {
        if (e?.key?.toUpperCase() === 'ENTER') {
          if (enterState) {
            const { activeItemId, collections } = enterState
            const value = get(collections, `[0].items[${activeItemId}].value`)

            if (value) {
              search.setQuery(value)
              props.onSubmit()
            }
          }
        }
      }}
    />
  )
}

interface OnClick {
  (s: string): any
}

interface AlgoliaAutoCompletePropTypes {
  onChange: OnClick
  initialValue?: string
  facet: 'brand' | 'product_name' | 'amount' | 'brand_subtype' | 'dosage'
  filters: string
  returnFocus?: any
  hasNotSet?: boolean
  onSubmit?: any
}

let _missingEnvVarsAlertShown = false

export const AlgoliaAutoComplete = ({
  onChange,
  facet,
  filters,
  initialValue,
  returnFocus,
  onSubmit,
  hasNotSet,
}: AlgoliaAutoCompletePropTypes) => {
  const appId = process.env.REACT_APP_ALGOLIA_APP_ID ?? ''
  const apiKey = process.env.REACT_APP_ALGOLIA_API_KEY ?? ''
  const indexName = process.env.REACT_APP_ALGOLIA_INDEX ?? ''
  if (!appId || !apiKey || !indexName) {
    if (!_missingEnvVarsAlertShown) {
      alert('BUG: Missing required Algolia environment variables!')
      console.error('BUG: Missing required Algolia environment variables!')
      _missingEnvVarsAlertShown = true
    }
  }
  const searchClient = algoliasearch(appId, apiKey)
  const index = searchClient.initIndex(indexName)

  return (
    <AlgoliaHandler
      onChange={onChange}
      onSubmit={onSubmit}
      initialValue={initialValue}
      getSources={({ query, setQuery, refresh }: any) => [
        {
          sourceId: 'products',
          getItems() {
            return index
              .searchForFacetValues(facet, query, {
                filters: filters?.trim() ? filters + ' AND canonical_product' : 'canonical_product',
              })
              .then(({ facetHits }) => {
                let items: any = [...facetHits]

                if (hasNotSet) {
                  items.unshift({
                    highlighted: <em>Not Set</em>,
                    value: 'Not Set',
                    count: 1,
                  })
                }

                return items
              })
          },
          templates: {
            item({ item, components }: any) {
              return (
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    setQuery(item.value)
                    returnFocus()
                    refresh()
                  }}
                  style={{ width: '100%', height: '100%' }}
                >
                  <a
                    href={item.url}
                    className="aa-ItemLink"
                    onKeyDown={(e: any) => {
                      if (e?.key?.toUpperCase() === 'ENTER') {
                        setQuery(item.value)
                        refresh()
                        returnFocus()
                      }
                    }}
                  >
                    <div className="aa-ItemContent">
                      <div className="aa-ItemTitle">
                        <components.Highlight attribute="value" hit={item} />
                      </div>
                    </div>
                  </a>
                </div>
              )
            },
          },
        },
      ]}
    />
  )
}
